/* eslint-disable @typescript-eslint/ban-ts-comment */
import { DestroyRef, ElementRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ResizeObserverService } from '@frontend/sports/common/core/utils/dom';
import { GridBreakpointSize, GridLayout } from '@frontend/sports/grid/core/feature/model';
import { MediaQueryService } from '@frontend/vanilla/core';
import { isEqual } from 'lodash-es';
import { Observable, distinctUntilChanged, map } from 'rxjs';

interface ColumnsBreakpoint {
    query: string;
    min?: number;
    max?: number;
    columns: number;
}

interface GridBreakpoint {
    [breakpoint: string]: number;
}

export interface GridBreakpointOptions {
    calculateGridWidth: (containerWidth: number) => number;
}

const DEFAULT_GRID_BREAKPOINTS_OPTIONS: GridBreakpointOptions = {
    calculateGridWidth: (containerWidth: number) => containerWidth,
};

const DEFAULT_GRID_BREAKPOINTS: GridBreakpoint = {
    'xs': 1,
    'sm': 2,
    'md': 3,
    'gt-md': 4,
};

const SIX_PACK_GRID_BREAKPOINTS: GridBreakpoint = {
    'lt-md': 1,
    'gt-sm': 2,
};

@Injectable({ providedIn: 'root' })
export class GridBreakpointService {
    private mediaObserver = inject(MediaQueryService);
    private resizeObserver = inject(ResizeObserverService);
    private container = inject(ElementRef, { optional: true });

    private static DEFAULT_BREAKPOINTS: ColumnsBreakpoint[];
    private static SIX_PACK_BREAKPOINTS: ColumnsBreakpoint[];

    private get defaultBreakpoints(): ColumnsBreakpoint[] {
        return (
            GridBreakpointService.DEFAULT_BREAKPOINTS ??
            (GridBreakpointService.DEFAULT_BREAKPOINTS = this.parseGridBreakpoints(DEFAULT_GRID_BREAKPOINTS))
        );
    }

    private get sixPackBreakpoints(): ColumnsBreakpoint[] {
        return (
            GridBreakpointService.SIX_PACK_BREAKPOINTS ??
            (GridBreakpointService.SIX_PACK_BREAKPOINTS = this.parseGridBreakpoints(SIX_PACK_GRID_BREAKPOINTS))
        );
    }

    private readonly destroyRef = inject(DestroyRef);

    forGrid(
        element: ElementRef,
        layout: GridLayout,
        options: GridBreakpointOptions = DEFAULT_GRID_BREAKPOINTS_OPTIONS,
    ): Observable<GridBreakpointSize> {
        const containerWidth = this.container
            ? this.resizeObserver.observe(this.container).pipe(
                  map((changes) => changes.contentRect.width),
                  distinctUntilChanged(),
                  map((width) => options.calculateGridWidth(width)),
              )
            : this.mediaObserver.observe().pipe(map(() => element.nativeElement.clientWidth));

        return containerWidth.pipe(
            map((width) => {
                const columnsBreakpoints = layout === GridLayout.SixPack ? this.sixPackBreakpoints : this.defaultBreakpoints;

                return {
                    default: this.matchColumnsBreakpoint(this.defaultBreakpoints, width),
                    columns: this.matchColumnsBreakpoint(columnsBreakpoints, width),
                };
            }),
            distinctUntilChanged(isEqual),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    private matchColumnsBreakpoint(source: ColumnsBreakpoint[], width?: number): number {
        const matcher = width
            ? (item: ColumnsBreakpoint) => (item.min === undefined || item.min <= width) && (item.max === undefined || item.max >= width)
            : (item: ColumnsBreakpoint) => this.mediaObserver.isActive(item.query);

        for (const current of source) {
            if (matcher(current)) {
                return current.columns;
            }
        }

        return 1;
    }

    private parseGridBreakpoints(source: GridBreakpoint): ColumnsBreakpoint[] {
        return Object.entries(this.mediaObserver.breakpoints)
            .filter((point) => point[0] in source)
            .map(([key, value]) => this.parseColumnsBreakpoint(key, value, source))
            .filter((point) => point.min || point.max)
            .sort((first, second) => second.columns - first.columns);
    }

    private parseColumnsBreakpoint(name: string, query: string, source: GridBreakpoint): ColumnsBreakpoint {
        const regex = /(min|max)-width:\s*(\d+)px/gi;
        let match: RegExpExecArray | null;

        const result: ColumnsBreakpoint = { query: name, columns: source[name] };

        while ((match = regex.exec(query))) {
            //@ts-ignore-error using match[1] as key index for result doesn't work, add expect error to bypass compiler issues.
            result[match[1]] = parseInt(match[2]);
        }

        return result;
    }
}
